/**
 * This is a wrapper component for different Layouts.
 * Navigational 'aside' content should be added to this wrapper.
 */
import React, { useEffect, useState } from 'react';
import { compose } from 'redux';
import { useSelector } from 'react-redux';

import { FormattedMessage } from '../../../util/reactIntl';
import { withViewport } from '../../../util/uiHelpers';
import { getBusinessListingParams } from '../../../util/data';

import { TabNav } from '../../../components';

import { createGlobalState } from './hookGlobalState';

import css from './LayoutSideNavigation.module.css';

const MAX_HORIZONTAL_NAV_SCREEN_WIDTH = 1023;
const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout

// Add global state for tab scrolling effect
const initialScrollState = { scrollLeft: 0 };
const { useGlobalState } = createGlobalState(initialScrollState);

// Horizontal scroll animation using element.scrollTo()
const scrollToTab = (currentPage, scrollLeft, setScrollLeft) => {
  const el = document.querySelector(`#${currentPage}Tab`);

  if (el) {
    // el.scrollIntoView doesn't work with Safari and it considers vertical positioning too.
    // This scroll behaviour affects horizontal scrolling only
    // and it expects that the immediate parent element is scrollable.
    const parent = el.parentElement;
    const parentRect = parent.getBoundingClientRect();
    const maxScrollDistance = parent.scrollWidth - parentRect.width;

    const hasParentScrolled = parent.scrollLeft > 0;
    const scrollPositionCurrent = hasParentScrolled ? parent.scrollLeft : scrollLeft;

    const tabRect = el.getBoundingClientRect();
    const diffLeftBetweenTabAndParent = tabRect.left - parentRect.left;
    const tabScrollPosition = parent.scrollLeft + diffLeftBetweenTabAndParent;

    const scrollPositionNew =
      tabScrollPosition > maxScrollDistance
        ? maxScrollDistance
        : parent.scrollLeft + diffLeftBetweenTabAndParent;

    const needsSmoothScroll = scrollPositionCurrent !== scrollPositionNew;

    if (parent.scrollTo && (!hasParentScrolled || (hasParentScrolled && needsSmoothScroll))) {
      // Ensure that smooth scroll animation uses old position as starting point after navigation.
      parent.scrollTo({ left: scrollPositionCurrent });
      // Scroll to new position
      parent.scrollTo({ left: scrollPositionNew, behavior: 'smooth' });
    }
    // Always keep track of new position (even if smooth scrolling is not applied)
    setScrollLeft(scrollPositionNew);
  }
};

/**
 * Side nav with navigation to different account settings.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.accountSettingsNavProps
 * @param {string?} props.accountSettingsNavProps.currentPage
 * @param {boolean?} props.accountSettingsNavProps.showPaymentMethods
 * @param {boolean?} props.accountSettingsNavProps.showPayoutDetails
 * @returns {JSX.Element} Side nav with navigation to different account settings
 */
const LayoutWrapperAccountSettingsSideNav = props => {
  const {
    viewport,
  } = props;
  
  const [mounted, setMounted] = useState(false);
  const [scrollLeft, setScrollLeft] = useGlobalState('scrollLeft');
  const { accountSettingsNavProps, ariaLabel } = props;
  const [isManualScroll, setIsManualScroll] = React.useState(false);
  const { currentPage } = accountSettingsNavProps || {};

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (mounted) {
      const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
      const hasHorizontalTabLayout = hasMatchMedia
        ? window.matchMedia(`(max-width: ${MAX_HORIZONTAL_NAV_SCREEN_WIDTH}px)`)?.matches
        : true;

      // Check if scrollToTab call is needed (tab is not visible on mobile)
      if (hasHorizontalTabLayout) {
        scrollToTab(currentPage, scrollLeft, setScrollLeft);
      }
    }
    let scrollTimeout = null;

    const { width } = viewport;
    const hasViewport = width > 0;
    const hasHorizontalTabLayout = hasViewport && width <= MAX_HORIZONTAL_NAV_SCREEN_WIDTH;

    const handleScroll = () => {
      setIsManualScroll(true);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      // scrollTimeout = setTimeout(() => {
      //   setIsManualScroll(false);
      // }, 2000);
    };

    const el = document.querySelector(`#${currentPage}Tab`);
    if (el && el.parentElement) {
      el.parentElement.addEventListener('scroll', handleScroll);
    }

    if (hasHorizontalTabLayout && !isManualScroll) {
      scrollTimeout = window.setTimeout(() => {
        scrollToTab(currentPage, scrollLeft, setScrollLeft);
      }, 300);
    }

    return () => {
      // Update scroll position when unmounting
      const el = document.querySelector(`#${currentPage}Tab`);
      if (el && el.parentElement) {
        el.parentElement.removeEventListener('scroll', handleScroll);
        setScrollLeft(el.parentElement.scrollLeft);
      }
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [props, scrollLeft, setScrollLeft, isManualScroll, mounted]);

  const { currentUser } = useSelector(state => state.user);
  const { profile } = (currentUser && currentUser.id && currentUser.attributes) || {};
  const { protectedData } = profile || {};
  const { businessListingId } = protectedData || {};
  const tabs = [];
  const isWindowDefined = typeof window !== 'undefined';
  const isMobileLayout = isWindowDefined && window.innerWidth < MODAL_BREAKPOINT;

  // if (isMobileLayout) {
  //   tabs.push(...[{
  //     text: <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.profileSettings" />,
  //     selected: currentPage === 'ProfileSettingsPage',
  //     id: 'ProfileSettingsPageTab',
  //     linkProps: {
  //       name: 'ProfileSettingsPage',
  //     },
  //   },
  //   {
  //     text: <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.editApplication" />,
  //     selected: currentPage === 'SignUpStepsPage',
  //     id: 'SignUpStepsPageTab',
  //     linkProps: {
  //       name: 'SignUpStepsPage',
  //       params: { tab: "detail" }
  //     },
  //   }
  //   ]);
  // }

  // if (businessListingId) {
  //   tabs.push({
  //     text: <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.editBusiness" />,
  //     selected: currentPage === 'EditListingPage',
  //     id: 'EditListingPageTab',
  //     linkProps: {
  //       name: 'EditListingPage',
  //       params: getBusinessListingParams(currentUser)
  //     },
  //   });
  // }


  tabs.push(...[
    {
      text: <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.compnanyProfile" />,
      selected: currentPage === 'CompanyProfilePage',
      id: 'CompanyProfilePageTab',
      linkProps: {
        name: 'CompanyProfilePage',
      },
    },
    {
      text: <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.contactDetailsTabTitle" />,
      selected: currentPage === 'ContactDetailsPage',
      id: 'ContactDetailsPageTab',
      linkProps: {
        name: 'ContactDetailsPage',
      },
    },
    {
      text: <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.passwordTabTitle" />,
      selected: currentPage === 'PasswordChangePage',
      id: 'PasswordChangePageTab',
      linkProps: {
        name: 'PasswordChangePage',
      },
    },
    {
      text: <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.paymentsTabTitle" />,
      selected: currentPage === 'StripePayoutPage',
      id: 'StripePayoutPageTab',
      linkProps: {
        name: 'StripePayoutPage',
      },
    },
    {
      text: <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.paymentMethodsTabTitle" />,
      selected: currentPage === 'PaymentMethodsPage',
      id: 'PaymentMethodsPageTab',
      linkProps: {
        name: 'PaymentMethodsPage',
      },
    },
  ]);

  return (
    <TabNav rootClassName={css.tabs} tabRootClassName={css.tab} tabs={tabs} ariaLabel={ariaLabel} />
  );
};

export default compose(withViewport)(LayoutWrapperAccountSettingsSideNav);
